import axios from "axios";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Banner, Button, Typography } from "@foodpilot/foods";

import { useGenerateAiComment } from "src/api/client-api/aiComments/useGenerateAiComment";
import { useOrganizationDiagnosticFacts } from "src/api/client-api/diagnosticFacts/useOrganizationDiagnosticFacts";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import isAppInPDFMode from "src/utils/rendering";
import { Loading } from "src/components/utils/Loading";
import { usePuckCompanyScore } from "src/components/Puck/config/contexts/PuckCompanyScoreContext";
import {
    extractParameterKeys,
    getTokenNamespaces,
    getUnsupportedParameterKeys,
    hasCalcParameterKeys,
} from "./extractParameterKeys";

const commentaryErrorKey = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 503 || status === 429) {
            return "puck.blocks.ai_comment.unavailable";
        }
    }

    return "puck.blocks.ai_comment.error";
};

const isNamespaceMissing = (
    namespace: string,
    context: {
        hasCompany: boolean;
        hasCampaign: boolean;
    },
): boolean => {
    switch (namespace) {
        case "company":
            return !context.hasCompany;
        case "campaign":
            return !context.hasCampaign;
        default:
            return false;
    }
};

export type AiCommentProps = {
    editorialPrompt: string;
    isEditing?: boolean;
};

export const AiComment = (props: AiCommentProps) => {
    const { editorialPrompt, isEditing = false } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const foodpilotContext = useFoodpilotContext();
    const scoreContext = usePuckCompanyScore();
    const { isLoading: isLoadingFacts } = useOrganizationDiagnosticFacts(editorialPrompt);

    const parameterKeys = useMemo(() => extractParameterKeys(editorialPrompt), [editorialPrompt]);
    const unsupportedKeys = useMemo(() => getUnsupportedParameterKeys(parameterKeys), [parameterKeys]);
    const usesCalcTokensFromKeys = useMemo(() => hasCalcParameterKeys(parameterKeys), [parameterKeys]);
    const ssqVersionId = scoreContext.companyPost?.ssqVersionId;
    const organizationId = foodpilotContext.selectedCompany?.id;

    const missingCalcContext = useMemo(() => {
        if (!usesCalcTokensFromKeys) {
            return false;
        }

        return (
            !foodpilotContext.currentCampaign?.id ||
            typeof ssqVersionId !== "number" ||
            typeof organizationId !== "number"
        );
    }, [foodpilotContext.currentCampaign?.id, organizationId, ssqVersionId, usesCalcTokensFromKeys]);

    const namespaceContext = useMemo(
        () => ({
            hasCompany: Boolean(foodpilotContext.selectedCompany),
            hasCampaign: Boolean(foodpilotContext.currentCampaign),
        }),
        [foodpilotContext.currentCampaign, foodpilotContext.selectedCompany],
    );
    const missingNamespaces = useMemo(
        () => getTokenNamespaces(parameterKeys).filter((namespace) => isNamespaceMissing(namespace, namespaceContext)),
        [namespaceContext, parameterKeys],
    );

    const generationRequest = useMemo(() => {
        if (parameterKeys.length === 0) {
            return null;
        }

        return {
            editorialPrompt,
            parameterKeys,
            ssqVersionId: usesCalcTokensFromKeys ? ssqVersionId : undefined,
            entityType: usesCalcTokensFromKeys ? "Organization" : undefined,
            entityId: usesCalcTokensFromKeys ? organizationId : undefined,
        };
    }, [editorialPrompt, organizationId, parameterKeys, ssqVersionId, usesCalcTokensFromKeys]);

    const canGenerate =
        !isEditing &&
        unsupportedKeys.length === 0 &&
        missingNamespaces.length === 0 &&
        !missingCalcContext &&
        (!usesCalcTokensFromKeys || !isLoadingFacts);

    const { data, isLoading, isError, error, refetch, isFetched } = useGenerateAiComment(
        generationRequest,
        canGenerate,
    );

    const isReady = Boolean(data?.text) && !isLoading;
    const showPdfReadyMarker = isAppInPDFMode() && isReady;

    if (parameterKeys.length === 0) {
        return (
            <Banner variant="info">
                <Typography variant="body2">
                    {t(
                        "puck.blocks.ai_comment.empty_prompt",
                        "Add editorial instructions with tokens to generate a comment.",
                    )}
                </Typography>
            </Banner>
        );
    }

    if (unsupportedKeys.length > 0) {
        return (
            <Banner variant="info">
                <Typography variant="body2" data-testid="ai-comment-unsupported-tokens">
                    {t(
                        "puck.blocks.ai_comment.unsupported_tokens",
                        "These tokens cannot be sent to the AI: {{tokens}}. Use company, campaign, pdf or calc tokens.",
                        { tokens: unsupportedKeys.join(", ") },
                    )}
                </Typography>
            </Banner>
        );
    }

    if (usesCalcTokensFromKeys && isLoadingFacts) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
                <Loading size={18} />
                <Typography variant="body2">
                    {t("puck.blocks.ai_comment.loading_facts", "Loading diagnostic data...")}
                </Typography>
            </Box>
        );
    }

    if (missingCalcContext) {
        return (
            <Banner variant="info">
                <Typography variant="body2" data-testid="ai-comment-missing-olap-context">
                    {t(
                        "puck.blocks.ai_comment.missing_olap_context",
                        "This block uses diagnostic score tokens (calc.*). Select an active campaign with a completed calculation questionnaire for this organization.",
                    )}
                </Typography>
            </Banner>
        );
    }

    if (missingNamespaces.length > 0) {
        return (
            <Banner variant="info">
                <Typography variant="body2" data-testid="ai-comment-missing-context">
                    {t(
                        "puck.blocks.ai_comment.missing_context",
                        "This block needs data that is not available in the current extension slot ({{namespaces}}).",
                        { namespaces: missingNamespaces.join(", ") },
                    )}
                </Typography>
            </Banner>
        );
    }

    return (
        <Box
            data-testid="ai-comment"
            data-pdf-ready={showPdfReadyMarker ? "true" : undefined}
            sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(1.5) }}
        >
            <Typography component="span" variant="caption" color="text.secondary">
                {t("puck.blocks.ai_comment.badge", "AI generated")}
            </Typography>

            {isLoading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
                    <Loading size={18} />
                    <Typography variant="body2">
                        {t("puck.blocks.ai_comment.loading", "Generating commentary...")}
                    </Typography>
                </Box>
            )}

            {isError && (
                <Banner variant="error">
                    <Box sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(1) }}>
                        <Typography variant="body2">
                            {commentaryErrorKey(error) === "puck.blocks.ai_comment.unavailable" ?
                                t(
                                    "puck.blocks.ai_comment.unavailable",
                                    "The commentary service is unavailable. Try again later.",
                                )
                            :   t("puck.blocks.ai_comment.error", "Unable to generate the AI commentary.")}
                        </Typography>
                        <Button variant="ternary" size="small" onClick={() => refetch()}>
                            {t("puck.blocks.ai_comment.retry", "Retry")}
                        </Button>
                    </Box>
                </Banner>
            )}

            {data?.text && (
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {data.text}
                </Typography>
            )}

            {!isLoading && !data?.text && isFetched && !isError && (
                <Typography variant="body2" color="text.secondary">
                    {t("puck.blocks.ai_comment.empty_result", "No commentary was generated.")}
                </Typography>
            )}
        </Box>
    );
};
